import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { useDashboardData } from "./DashboardData";
import { WorkerClientBalance, workerClientBalances } from "./dashboardOperationalData";

interface Props {
    periodDays?: number;
}

const clientPalette = [
    "#2563eb",
    "#b45309",
    "#047857",
    "#7c3aed",
    "#be123c",
    "#0e7490",
    "#4f46e5",
    "#15803d",
    "#c2410c",
    "#9333ea",
    "#0f766e",
    "#9f1239",
];

export const workerClientColorMap = (balances: WorkerClientBalance[]) => {
    const clients = new Map<string, string>();
    balances.forEach(balance => balance.shares.forEach(share => clients.set(share.clientKey, share.clientName)));
    return new Map(Array.from(clients.entries())
        .sort((left, right) => left[1].localeCompare(right[1]) || left[0].localeCompare(right[0]))
        .map(([clientKey], index) => [clientKey, clientPalette[index % clientPalette.length]]));
};

const WorkerClientBalanceCard: React.FC<Props> = ({ periodDays = 90 }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const { accounts, accountsFailed, accountsLoading, projects, projectsFailed, projectsLoading } = useDashboardData();
    const balances = useMemo(() => workerClientBalances(projects, accounts, periodDays), [accounts, periodDays, projects]);
    const clientColors = useMemo(() => workerClientColorMap(balances), [balances]);
    const loading = accountsLoading || projectsLoading;
    const failed = accountsFailed || projectsFailed;
    const percentage = (value: number) => new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 1 }).format(value);
    const formatHours = (minutes: number) => t("accounts.accounts.hoursShort", {
        hours: new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 2 }).format(minutes / 60),
    });

    return <Card className="h-100 mb-0 dashboard-widget-card">
        <CardBody>
            <CardHeader
                title={t("dashboard.workerClientBalanceCard.title")}
                icon="mdi-scale-balance"
                count={balances.length}
                actions={<Link to="/projects" className="btn btn-sm btn-light" aria-label={t("dashboard.workerClientBalanceCard.openProjects")}>
                    <i className="mdi mdi-arrow-right" aria-hidden="true" />
                </Link>}
            />
            <p className="small text-muted mt-3 mb-1">{t("dashboard.workerClientBalanceCard.intro", { count: periodDays })}</p>
            {loading && <p className="text-muted mt-2 mb-0">{t("dashboard.workerClientBalanceCard.loading")}</p>}
            {failed && <p className="text-danger mt-2 mb-0">{t("dashboard.workerClientBalanceCard.loadFailed")}</p>}
            {!loading && !failed && balances.length === 0 && <p className="text-muted mt-2 mb-0">{t("dashboard.workerClientBalanceCard.empty")}</p>}
            {!loading && !failed && <div className="dashboard-widget-scroll dashboard-balance-list">
                {balances.map(balance => <div className="dashboard-balance-row" key={balance.workerAccountId}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <span className="dashboard-balance-worker">
                            <Link to={`/accounts/${balance.workerAccountId}`} className="text-body fw-semibold d-block text-truncate">
                                {balance.workerName}
                            </Link>
                            <small className="text-muted">{t("dashboard.workerClientBalanceCard.total", { hours: formatHours(balance.totalMinutes) })}</small>
                        </span>
                        <span
                            className="dashboard-balance-percentage flex-shrink-0"
                            style={{ backgroundColor: clientColors.get(balance.shares[0]?.clientKey) }}
                        >
                            {percentage(balance.dominantPercentage)}%
                        </span>
                    </div>
                    <div className="dashboard-balance-track mt-2" aria-hidden="true">
                        {balance.shares.map(share => <span
                            className="dashboard-balance-segment"
                            key={share.clientKey}
                            style={{ backgroundColor: clientColors.get(share.clientKey), width: `${share.percentage}%` }}
                        />)}
                    </div>
                    <div className="dashboard-balance-ratio mt-2">
                        {balance.shares.map(share => <div className="dashboard-balance-share" key={share.clientKey}>
                            <span
                                className="dashboard-balance-share-dot"
                                style={{ backgroundColor: clientColors.get(share.clientKey) }}
                                aria-hidden="true"
                            />
                            <span className="dashboard-balance-share-name" title={share.clientName}>{share.clientName}</span>
                            <strong className="dashboard-balance-share-value">{percentage(share.percentage)}%</strong>
                        </div>)}
                    </div>
                </div>)}
            </div>}
        </CardBody>
    </Card>;
};

export default WorkerClientBalanceCard;
